// Capability tokens, read back after the head guard took them out of the URL.

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export type CapabilityRoute =
  | 'invite'
  | 'share'
  | 'survey'
  | 'founder-followup'
  | 'join'
  | 'magic-link';

export const CAPABILITY_TOKEN_SENTINEL = '~';

/** The `Authorization` scheme the API's public token routes read. */
export const CAPABILITY_TOKEN_SCHEME = 'ZoeToken';

/** The path segment a public capability-token call carries its token in. */
export function capabilityTokenPathSegment(_token: string): string {
  return CAPABILITY_TOKEN_SENTINEL;
}

const STORAGE_PREFIX = 'zoe.cap.';

/** See THE CONTRACT WITH THE GUARD above — token-guard.js builds this same key. */
export function capabilityTokenStorageKey(route: CapabilityRoute): string {
  return `${STORAGE_PREFIX}${route}`;
}

/** Forget only the redeemed token; another tab may have parked a newer link. */
export function clearStoredCapabilityToken(route: CapabilityRoute, token: string): void {
  if (!token || typeof window === 'undefined') return;
  const key = capabilityTokenStorageKey(route);
  for (const area of ['sessionStorage', 'localStorage'] as const) {
    try {
      const storage = window[area];
      if (storage.getItem(key) === token) storage.removeItem(key);
    } catch {
      // Blocked storage must not interrupt a successful sign-in or the other store's cleanup.
    }
  }
}

/** Routes whose link has always carried `?token=` rather than a path segment. */
const QUERY_CARRIED: ReadonlySet<CapabilityRoute> = new Set<CapabilityRoute>(['magic-link']);

const SURVIVES_A_TAB_HOP: ReadonlySet<CapabilityRoute> = new Set<CapabilityRoute>([
  'join',
  'magic-link',
]);

function claimTabHopToken(key: string): string {
  let carried = '';
  try {
    carried = window.localStorage.getItem(key) ?? '';
  } catch {
    // Blocked site data. Nothing to claim; the caller renders its dead link.
    return '';
  }
  if (!carried) return '';
  try {
    window.sessionStorage.setItem(key, carried);
    if (window.sessionStorage.getItem(key) === carried) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // This tab cannot hold a copy of its own.
  }
  return carried;
}

/** The token parked for `route`, or '' when storage cannot answer. */
export function readStoredCapabilityToken(route: CapabilityRoute): string {
  if (typeof window === 'undefined') return '';
  const key = capabilityTokenStorageKey(route);
  try {
    const parked = window.sessionStorage.getItem(key);
    if (parked) return parked;
  } catch {
    // Storage blocked (private window, site-data policy).
  }
  if (!SURVIVES_A_TAB_HOP.has(route)) return '';
  return claimTabHopToken(key);
}

/** The resolution rule on its own, free of React, so it can be reasoned about and tested without a router. */
export function resolveCapabilityToken(
  route: CapabilityRoute,
  carried: string | undefined,
): string {
  const value = carried ?? '';
  if (value && value !== CAPABILITY_TOKEN_SENTINEL) return value;
  if (!value && !QUERY_CARRIED.has(route)) return '';
  return readStoredCapabilityToken(route);
}

export function useCapabilityToken(route: CapabilityRoute): string {
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const carried = QUERY_CARRIED.has(route) ? searchParams.get('token') ?? '' : params.token;
  return useMemo(() => resolveCapabilityToken(route, carried), [route, carried]);
}

/** Headers that carry `token` to the API in place of a path segment. */
export function capabilityTokenHeaders(token: string): Record<string, string> {
  return token ? { Authorization: `${CAPABILITY_TOKEN_SCHEME} ${token}` } : {};
}

/**
 * Request init for a public capability-token call: the token rides the Authorization header,
 * and the reply is never served from the browser cache. Every token for a route shares one URL
 * (`/api/share/~`), so a cached reply would answer for whichever link was opened last.
 */
export function capabilityTokenInit(token: string, init: RequestInit = {}): RequestInit {
  return {
    ...init,
    cache: 'no-store',
    headers: {
      ...capabilityTokenHeaders(token),
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    },
  };
}
